import { Currency } from "@redotech/money/currencies";
import {
  MoneyValue,
  moneyValueToNumber,
} from "@redotech/money/money-calculator";
import { assertNever } from "@redotech/util/type";
import { AdjustmentType } from "../../return";
import { ProductTotals, ReturnTotals } from "../../return-totals-calculator";
import {
  ProductSummaryTotals,
  ReturnSummaryTotals,
  SerializedProductSummaryTotals,
  SerializedReturnSummaryTotals,
} from "./types";

export const RAC_LOG_STAMP = "RAC DARK LAUNCH";

export enum ReturnTotalsType {
  LEGACY = "legacy",
  NEW = "new",
}

type NumberKeys<T> = {
  [K in keyof T]: T[K] extends number ? K : never;
}[keyof T];

const fieldsMatch = (
  legacyField: number,
  newField: number | MoneyValue<Currency>,
): boolean => {
  const newFieldValue =
    typeof newField === "number" ? newField : newFieldValueToNumber(newField);
  // Only log out differences greater than 0.05, the ones I've checked that are less than 0.05, RAC is correct
  // and RTC is wrong
  return Math.abs(legacyField - newFieldValue) < 0.05;
};

const newFieldValueToNumber = (
  newField: number | MoneyValue<Currency>,
): number => {
  return typeof newField === "number" ? newField : moneyValueToNumber(newField);
};

export const compareReturnTotals = (returnTotalsSet: ReturnTotalsSet) => {
  const discrepancies: {
    legacyField: { value: number; name: string };
    newField: { value: number; name: string };
    difference: number;
  }[] = [];
  const returnFieldsToCompare: NumberKeys<ReturnTotals>[] = [
    "totalReturnValue",
    "totalStoreCredit",
    "totalRefundWithoutShipping",
    "totalRawReturnValue",
    "newOrderValue",
    "rawNewOrderValue",
    "displayCharge",
    "nonAccruableCredit",
  ];

  for (const field of returnFieldsToCompare) {
    const legacyValue = returnTotalsSet.legacy[field];
    const newField = mapOldReturnFieldToNewField(field, returnTotalsSet);
    if (!newField) {
      continue;
    }
    if (!fieldsMatch(legacyValue, newField.value)) {
      discrepancies.push({
        legacyField: { value: legacyValue, name: field },
        newField: {
          value: newFieldValueToNumber(newField.value),
          name: newField.name,
        },
        difference: Math.abs(
          legacyValue - newFieldValueToNumber(newField.value),
        ),
      });
    }
  }

  const productFieldsToCompare: NumberKeys<ProductTotals>[] = [
    "discountPrice",
    "originalPrice",
    "priceAdjustment",
    "merchantAdjustment",
    "finalPrice",
    "tax",
    "totalDiscount",
    "refund_item",
    "refund_tax",
    "total_refund",
  ];

  for (const productTotals of returnTotalsSet.legacy.productTotals) {
    const newProductTotals = returnTotalsSet.new?.productTotalsList.find(
      (p) => p.id === productTotals.product._id?.toString(),
    );
    for (const field of productFieldsToCompare) {
      const legacyValue = productTotals[field];
      const newField = mapOldProductFieldToNewField(field, {
        legacy: productTotals,
        new: newProductTotals,
        typeToUse: returnTotalsSet.typeToUse,
      });
      if (!newField) {
        continue;
      }
      if (!fieldsMatch(legacyValue, newField.value)) {
        discrepancies.push({
          legacyField: { value: legacyValue, name: field },
          newField: {
            value: newFieldValueToNumber(newField.value),
            name: newField.name,
          },
          difference: Math.abs(
            legacyValue - newFieldValueToNumber(newField.value),
          ),
        });
      }
    }
  }
  return discrepancies;
};

// ------------ Return Totals Set ------------ //

type NewReturnTotals =
  | ReturnSummaryTotals<Currency>
  | SerializedReturnSummaryTotals;

// Allows us to dark launch and hide return accounting calculator behind a feature flag
export type ReturnTotalsSet = {
  legacy: ReturnTotals;
  new: NewReturnTotals | undefined;
  typeToUse: ReturnTotalsType;
};

export const mapOldReturnFieldToNewField = (
  legacyFieldName: NumberKeys<ReturnTotals>,
  returnTotalsSet: ReturnTotalsSet,
): { name: string; value: number | MoneyValue<Currency> } | null => {
  if (!returnTotalsSet.new) {
    return null;
  }
  switch (legacyFieldName) {
    case "totalReturnValue":
      return {
        name: "returnValue.amount",
        value: newFieldValueToNumber(returnTotalsSet.new.returnValue.amount),
      };
    case "totalStoreCredit":
      return {
        name: "storeCredit.withoutOriginalOrderShipping",
        value: newFieldValueToNumber(
          returnTotalsSet.new.storeCredit.withoutOriginalOrderShipping,
        ),
      };
    case "totalRefundWithoutShipping":
      return {
        name: "refund.withoutOriginalOrderShipping",
        value: newFieldValueToNumber(
          returnTotalsSet.new.refund.withoutOriginalOrderShipping,
        ),
      };
    case "totalRawReturnValue":
      return {
        name: "returnValue.preTaxPreAdjustments",
        value: newFieldValueToNumber(
          returnTotalsSet.new.returnValue.preTaxPreAdjustments,
        ),
      };
    case "newOrderValue":
      return {
        name: "newOrderTotals.totalValue",
        value: newFieldValueToNumber(
          returnTotalsSet.new.newOrderTotals.totalValue,
        ),
      };
    case "rawNewOrderValue":
      return {
        name: "newOrderTotals.preTax",
        value: newFieldValueToNumber(returnTotalsSet.new.newOrderTotals.preTax),
      };
    case "displayCharge":
      return {
        name: "outstandingBalance",
        value: newFieldValueToNumber(returnTotalsSet.new.outstandingBalance),
      };
    case "nonAccruableCredit":
      return {
        name: "preDiscountShopNowBonus",
        value: newFieldValueToNumber(
          returnTotalsSet.new.preDiscountShopNowBonus,
        ),
      };
    case "totalStoreCreditWithShipping":
      return {
        name: "totalStoreCreditWithShipping",
        value: newFieldValueToNumber(
          returnTotalsSet.new.storeCredit.withOriginalOrderShipping,
        ),
      };
    case "totalRefundWithShipping":
      return {
        name: "refund.withOriginalOrderShipping",
        value: newFieldValueToNumber(
          returnTotalsSet.new.refund.withOriginalOrderShipping,
        ),
      };
    case "totalAdjustmentValue":
    case "totalMerchantAdjustment":
    case "totalDiscount":
    case "newOrderDiscount":
    case "totalNewOrderTaxes":
    case "advancedExchangeItemsTaxInNewOrder":
    case "totalTaxes":
    case "charge":
    case "recoveryFee":
    case "shippingFee":
    case "shippingFeeDeducted":
    case "shippingFeeDeductedFromStoreCredit":
    case "shippingFeeDeductedFromRefund":
    case "nonAccruableLeftover":
    case "finalSaleCredit":
    case "totalOrderShipping":
    case "totalPaymentOwed":
    case "totalLoyaltyPointsRefunded":
    case "totalLoyaltyPointsDeducted":
    case "totalReturnValueDeductedDueToLoyaltyPoints":
    case "exchangeShippingFee":
    case "totalRawTaxes":
    case "customerLabelUpchargeFee":
      return null;
    default:
      assertNever(legacyFieldName);
  }
};

const getSingleFieldFromReturnTotalsSet = ({
  returnTotalsSet,
  legacyFieldName,
}: {
  returnTotalsSet: ReturnTotalsSet;
  legacyFieldName: NumberKeys<ReturnTotals>;
}): number => {
  const legacyField = returnTotalsSet.legacy[legacyFieldName];
  if (!returnTotalsSet.new) {
    return legacyField;
  }
  const newField = mapOldReturnFieldToNewField(
    legacyFieldName,
    returnTotalsSet,
  );
  switch (returnTotalsSet.typeToUse) {
    case ReturnTotalsType.LEGACY:
      return legacyField;
    case ReturnTotalsType.NEW:
      if (!newField) {
        throw new Error(
          `New field not found for legacy field ${legacyFieldName}`,
        );
      }
      return newFieldValueToNumber(newField.value);
    default:
      assertNever(returnTotalsSet.typeToUse);
  }
};

export const getFieldsFromReturnTotalsSet = <
  T extends NumberKeys<ReturnTotals>,
>({
  returnTotalsSet,
  fields,
  logErrorFn = console.error,
}: {
  returnTotalsSet: ReturnTotalsSet;
  fields: T[];
  logErrorFn?: (message: string) => void;
}): Record<T, number> => {
  return fields.reduce(
    (acc, field) => {
      acc[field] = getSingleFieldFromReturnTotalsSet({
        returnTotalsSet,
        legacyFieldName: field,
      });
      return acc;
    },
    {} as Record<T, number>,
  );
};

// ------------ Product Totals Set ------------ //

type NewProductTotals =
  | SerializedProductSummaryTotals
  | ProductSummaryTotals<Currency>;
export type ProductTotalsSet = {
  legacy: ProductTotals;
  new: NewProductTotals | undefined;
  typeToUse: ReturnTotalsType;
};

const mapOldProductFieldToNewField = (
  legacyFieldName: NumberKeys<ProductTotals>,
  productTotalsSet: ProductTotalsSet,
): { name: string; value: number | MoneyValue<Currency> } | null => {
  if (!productTotalsSet.new) {
    return null;
  }
  switch (legacyFieldName) {
    case "finalPrice":
      return {
        name: "returnValue.amount",
        value: newFieldValueToNumber(productTotalsSet.new.returnValue.amount),
      };
    case "discountPrice":
      return {
        name: "originalValue.discountedPrice",
        value: newFieldValueToNumber(
          productTotalsSet.new.originalValue.discountedPrice,
        ),
      };
    case "originalPrice":
      return {
        name: "originalValue.price",
        value: newFieldValueToNumber(productTotalsSet.new.originalValue.price),
      };
    case "priceAdjustment":
      return {
        name: "adjustments.returnFlow",
        value: newFieldValueToNumber(
          productTotalsSet.new.adjustments.find(
            (adjustment) =>
              adjustment.type === AdjustmentType.CHOOSE_ITEMS_FLOW,
          )?.amount ?? 0,
        ),
      };
    case "merchantAdjustment":
      return {
        name: "adjustments.merchantManual",
        value: newFieldValueToNumber(
          productTotalsSet.new.adjustments.find(
            (adjustment) => adjustment.type === AdjustmentType.MERCHANT_MANUAL,
          )?.amount ?? 0,
        ),
      };
    case "tax":
      return {
        name: "originalValue.tax",
        value: newFieldValueToNumber(productTotalsSet.new.originalValue.tax),
      };
    case "totalDiscount":
      return {
        name: "originalValue.discount",
        value: newFieldValueToNumber(
          productTotalsSet.new.originalValue.discount,
        ),
      };
    case "rawTax":
    case "returnValueDeductedDueToLoyaltyPoints":
    case "loyaltyPointsRefunded":
    case "loyaltyPointsDeducted":
    case "total_refund":
    case "refund_tax":
    case "refund_item":
    case "dynamicBundleAdjustment":
      return null;

    default:
      assertNever(legacyFieldName);
  }
};

const getSingleFieldFromProductTotalsSet = ({
  productTotalsSet,
  legacyFieldName,
}: {
  productTotalsSet: ProductTotalsSet;
  legacyFieldName: NumberKeys<ProductTotals>;
}): number => {
  const legacyField = productTotalsSet.legacy[legacyFieldName];
  if (!productTotalsSet.new) {
    return legacyField;
  }
  const newField = mapOldProductFieldToNewField(
    legacyFieldName,
    productTotalsSet,
  );
  switch (productTotalsSet.typeToUse) {
    case ReturnTotalsType.LEGACY:
      return legacyField;
    case ReturnTotalsType.NEW:
      if (!newField) {
        throw new Error(
          `New field not found for legacy field ${legacyFieldName} for product ${productTotalsSet.legacy.product._id}`,
        );
      }
      return newFieldValueToNumber(newField.value);
    default:
      assertNever(productTotalsSet.typeToUse);
  }
};

export const getProductFieldsFromReturnTotalsSet = <
  T extends NumberKeys<ProductTotals>,
>({
  returnTotalsSet,
  productId,
  fields,
  logErrorFn = console.error,
}: {
  returnTotalsSet: ReturnTotalsSet;
  productId: string;
  fields: T[];
  logErrorFn?: (message: string) => void;
}): Record<T, number> | undefined => {
  const legacyProductTotals = returnTotalsSet.legacy.productTotals.find(
    (p) => p.product._id?.toString() === productId?.toString(),
  );
  if (!legacyProductTotals) {
    logErrorFn(
      `${RAC_LOG_STAMP} Legacy product totals not found for product ${productId}`,
    );
    return undefined;
  }
  const newProductTotals = returnTotalsSet.new?.productTotalsList.find(
    (p) => p.id === productId,
  );
  if (!newProductTotals) {
    logErrorFn(
      `${RAC_LOG_STAMP} New product totals not found for product ${productId}`,
    );
    if (returnTotalsSet.typeToUse === ReturnTotalsType.NEW) {
      throw new Error(`New product totals not found for product ${productId}`);
    }
  }
  const productTotalsSet = {
    legacy: legacyProductTotals,
    new: newProductTotals,
    typeToUse: returnTotalsSet.typeToUse,
  };
  return fields.reduce(
    (acc, field) => {
      acc[field] = getSingleFieldFromProductTotalsSet({
        productTotalsSet,
        legacyFieldName: field,
      });
      return acc;
    },
    {} as Record<T, number>,
  );
};
